import {
  SelectPaymentMethod,
  tokens,
} from "@/components/modals/contents/select-deposit-method";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useQueryClient } from "@tanstack/react-query";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useMemo, useState } from "react";
import { useWithdraw, useGetOTP } from "@/hooks/use-mutations";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { formatAmount as formatAmountUtil } from "@/lib/utils";
import { useGetRate } from "@/hooks/use-queries";
import { useUser } from "@/zustand/store";
import { useModalStore } from "@/zustand/modalStore";

const Withdraw = ({
  step,
  setStep,
  closeSheet,
}: {
  step: number;
  setStep: (step: number) => void;
  closeSheet: () => void;
}) => {
  const { user } = useUser((state) => state);
  const [activeToken, setActiveToken] = useState<Token | null>(null);
  const [withdrawalAddress, setWithdrawalAddress] = useState("");
  const selectedToken = tokens.find((token) => token.title === activeToken);
  const queryClient = useQueryClient();
  const { openModal } = useModalStore();
  const [amount, setAmount] = useState("");
  const [otp, setOtp] = useState("");
  const formatAmount = useFormatAmountWithCurrency();

  const preferredCurrency = user?.preferredCurrency || "USD";
  const { data: rateData } = useGetRate(preferredCurrency);
  const rate = rateData?.rate || 1;

  // Convert amount from preferred currency to USDT
  const usdtAmount = useMemo(() => {
    const numAmount = parseFloat(amount) || 0;
    if (preferredCurrency.toUpperCase() === "USD") {
      return numAmount;
    }
    return numAmount * rate;
  }, [amount, rate, preferredCurrency]);

  const { mutateAsync: getOtp, isPending: isGettingOtp } = useGetOTP();

  const { mutateAsync: withdraw, isPending: isWithdrawing } = useWithdraw({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deposits"],
      });
      queryClient.invalidateQueries({
        queryKey: ["recentTransactions"],
      });
      closeSheet();
      openModal("success", { type: "withdrawal" });
    },
  });

  const onSubmit = () => {
    withdraw({
      token: selectedToken?.coin,
      to: withdrawalAddress,
      amount: usdtAmount,
      chain: selectedToken?.network,
      otp: otp,
    });
  };

  return (
    <div className="mt-4 px-2 text-white">
      {step === 1 && (
        <div className="mt-6">
          <h2 className="font-semibold">Withdraw</h2>

          <div className="mt-4 text-xs">Wallet balance</div>
          <div className="text-2xl font-medium">
            {formatAmount(user?.balance || 0)}
          </div>

          <div className="mt-4 mb-2 text-xs">Select Payment Method</div>
          <SelectPaymentMethod
            activeOption={activeToken}
            setActiveOption={setActiveToken}
          />

          <h4 className="mt-4 text-xs">Withdrawal Address</h4>
          <Input
            value={withdrawalAddress}
            onChange={(e) => setWithdrawalAddress(e.target.value)}
            placeholder="Enter wallet address"
            className="bg-primary-500/60 border-border-neutral/30 mt-2 h-11 text-white placeholder:text-white lg:text-xs lg:placeholder:text-xs"
          />
          <p className="mt-1 text-[10px] leading-2.5 font-light">
            Please enter the address your funds withdrawn to, according to
            selected cryptocurrency and network selected
          </p>

          <h4 className="mt-4 text-xs">Amount ({preferredCurrency})</h4>
          <div className="relative">
            <Input
              value={amount}
              type="number"
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Enter Amount in ${preferredCurrency}`}
              className="bg-dark-input bg-dark-stroke-2 mt-2 h-11 lg:text-xs lg:placeholder:text-xs"
            />
            {amount && parseFloat(amount) > 0 && (
              <p className="text-muted-foreground mt-1 text-xs">
                ≈ {formatAmountUtil(usdtAmount)} USDT
              </p>
            )}
          </div>
          <p className="mt-1 text-[10px] leading-2.5 font-light">
            Withdrawal fee: 2%
          </p>

          <div className="mt-4 rounded-lg bg-[#2C2D2FE5] px-4 py-2 text-[10px]">
            <h6>Note</h6>
            <p className="mt-2 font-light">
              For your security withdrawal from your Tetheruz Wallet to your
              external wallet are processed manually and can take 24 hours.
              Please contact the support team with your account details and
              screenshot of your pending withdrawal to have it confirmed
            </p>
          </div>

          <Button
            onClick={async () => {
              await getOtp({
                emailAddress: user.email,
                purpose: "withdrawal",
                address: withdrawalAddress,
                amount: usdtAmount.toString(),
              });
              setStep(2);
            }}
            isLoading={isGettingOtp}
            disabled={
              isGettingOtp || !withdrawalAddress || !amount || !selectedToken
            }
            className="text-primary-500 bg-primary-100 hover:bg-primary-100-hover mt-6 mb-8 w-full font-semibold"
          >
            Continue
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6">
          <h2 className="font-semibold">Confirm Withdraw</h2>
          <div className="bg-primary-500/60 mt-2 rounded border border-[#BED3FF33] p-4 backdrop-blur-sm">
            <h5 className="text-xs">Preview</h5>
            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-y-4 *:w-1/2">
              <div>
                <div className="text-[10px] text-[#D4D4D4]">Payment Method</div>
                <div className="mt-0.5 flex items-center gap-2">
                  <img
                    src={selectedToken?.icon}
                    alt={selectedToken?.title}
                    className="size-4"
                  />
                  <span className="text-sm">{selectedToken?.altTitle}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-[#D4D4D4]">Network</div>
                <div className="mt-0.5">{selectedToken?.network}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#D4D4D4]">
                  Amount Tendered
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  {formatAmountUtil(usdtAmount)} {selectedToken?.coin}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-[#D4D4D4]">
                  Fee({user.withdrawalFee}%)
                </div>
                <div className="mt-0.5">
                  {formatAmountUtil(
                    usdtAmount * ((user.withdrawalFee || 0) / 100),
                  )}{" "}
                  {selectedToken?.coin}
                </div>
              </div>
              <div className="">
                <div className="text-[10px] text-[#D4D4D4]">
                  Recipient Will Recieve
                </div>
                <div className="mt-0.5">
                  {formatAmountUtil(
                    usdtAmount - (usdtAmount * (user.withdrawalFee || 0)) / 100,
                  )}
                  {selectedToken?.coin}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h1 className="text-2xl font-semibold">Let’s verify your Email</h1>
            <p className="text-sm leading-3.5 text-[#8C8C8C]">
              We’ve sent a 6-digit code to your email, enter the code below to
              verify
            </p>
            <InputOTP
              value={otp}
              onChange={(e) => setOtp(e)}
              id="otp"
              autoFocus
              autoComplete="off"
              pattern={REGEXP_ONLY_DIGITS}
              maxLength={6}
            >
              <InputOTPGroup className="*:bg-primary-500/80 mt-2 gap-3 *:!rounded *:border *:border-[#CED6E680] *:shadow-none">
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            <Button
              disabled={!otp || isWithdrawing}
              isLoading={isWithdrawing}
              onClick={() => onSubmit()}
              className="text-primary-500 bg-primary-100 hover:bg-primary-100-hover mt-6 mb-8 w-full font-semibold"
            >
              Authorize Payment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdraw;
